import { FC, PropsWithChildren } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import cn from "classnames";

import { Button, Buttons, Tag } from "capitalrx-components-2.0/lib/general";
import { Shelf, Stack } from "capitalrx-components-2.0/lib/layout";
import { Paragraph, Semibold } from "capitalrx-components-2.0/lib/typography";

import { QuestionPathStatus } from "@/utils";

import styles from "../../ClinicalCriteria.module.scss";

export interface QuestionnaireWrapperProps {
  readonly name: string;
  readonly disabled?: boolean;
  readonly isReadonly?: boolean;
  readonly questionNumber: number;
  readonly questionText: string;
  readonly isInternal?: boolean;
  readonly hideClearSelection?: boolean;
  readonly debugMode?: boolean;
  readonly questionPathStatus?: QuestionPathStatus;
}

export const QuestionWrapper: FC<PropsWithChildren<QuestionnaireWrapperProps>> = ({
  children,
  disabled = false,
  isReadonly = false,
  questionNumber,
  questionText,
  isInternal = false,
  name,
  hideClearSelection = false,
  debugMode,
  questionPathStatus,
}) => {

  const { setValue } = useFormContext();

  const value = useWatch({ name });

  function clearSelection() {
    setValue(name, null);
  }

  return (
    <Shelf
      spacing="small"
      alignment="top"
      className={disabled ? cn(styles.QuestionShelf, styles.Disabled) : styles.QuestionShelf}
      testId={`question_${name}`}
    >
      <Semibold testId="question_number">{`${questionNumber}.`}</Semibold>
      <Stack spacing="medium" alignment="left" className={styles.FullWidth}>
        <Paragraph className={styles.WhitespacePreWrap} testId="question_text">
          <Semibold>{questionText}</Semibold>
        </Paragraph>
        {isInternal && (
          <Tag className={styles.MaxContent} testId="question_internal">
            Internal Question
          </Tag>
        )}
        {debugMode && questionPathStatus && (
          <Tag className={styles.MaxContent} testId="question_path_status">
            {questionPathStatus}
          </Tag>
        )}
        {!hideClearSelection && value && !disabled && !isReadonly && (
          <Button
            className={styles.MaxContent}
            onClick={clearSelection}
            appearance={Buttons.Functional.Tertiary.Small}
            data-testid="question_clear_selection"
          >
            Clear Selection
          </Button>
        )}
        {children}
      </Stack>
      <Shelf.Spacer />
    </Shelf>
  );
};
